import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Trophy, Clock, AlertCircle, BarChart3, MapPin, DollarSign, Download, Star } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import AmbassadorPerformanceModal from '../components/AmbassadorPerformanceModal'

type Range = 'today' | '7d' | '30d' | 'all'

interface DriverRow {
  id: string
  full_name: string
  phone: string
  online: boolean
  orders_count: number
  earnings_fils: number
  tips_fils: number
  avg_delivery_minutes: number | null
  on_time_rate: number | null
  sample_size: number
  avg_rating: number | null
  rating_count: number
}

interface SlowDelivery {
  id: string
  order_number: string
  delivery_address: string | null
  driver_id: string | null
  driver_name: string | null
  out_for_delivery_at: string | null
  delivered_at: string | null
  delivery_minutes: number
}

interface Leaderboard {
  range: Range
  sla_minutes: number
  totals: { orders: number; earnings_fils: number }
  top_earners: DriverRow[]
  best_on_time: DriverRow[]
  slowest_deliveries: SlowDelivery[]
  min_sample_for_on_time: number
}

interface PayoutRow {
  id: string
  full_name: string
  phone: string
  orders_count: number
  earnings_fils: number
}

interface PayoutResponse {
  range: Range
  rows: PayoutRow[]
  grand_total: { orders: number; earnings_fils: number }
}

const formatJOD = (fils: number) => `JD ${(fils / 1000).toFixed(3)}`

const RANGE_LABELS: Record<Range, string> = {
  today: 'Today',
  '7d':  'Last 7 days',
  '30d': 'Last 30 days',
  all:   'All time',
}

export default function Performance() {
  const [range, setRange] = useState<Range>('7d')
  const [perfDriverId, setPerfDriverId] = useState<string | null>(null)

  const lb = useQuery<Leaderboard>({
    queryKey: ['perf-leaderboard', range],
    queryFn: () => api.get(`/performance/leaderboard?range=${range}`).then(r => r.data),
  })

  const payout = useQuery<PayoutResponse>({
    queryKey: ['perf-payout', range],
    queryFn: () => api.get(`/performance/payout?range=${range}`).then(r => r.data),
  })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-white">Performance</h1>
          <p className="text-xs text-gray-500 mt-0.5">Ambassador leaderboards, SLA breaches, and payout summaries</p>
        </div>
        <RangeTabs value={range} onChange={setRange} />
      </div>

      {/* Period totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <SummaryTile
          icon={<DollarSign size={16} />}
          label={`Total payout · ${RANGE_LABELS[range]}`}
          value={lb.data ? formatJOD(lb.data.totals.earnings_fils) : '—'}
          accent
        />
        <SummaryTile
          icon={<BarChart3 size={16} />}
          label={`Deliveries · ${RANGE_LABELS[range]}`}
          value={lb.data ? String(lb.data.totals.orders) : '—'}
        />
        <SummaryTile
          icon={<Clock size={16} />}
          label="SLA target"
          value={lb.data ? `${lb.data.sla_minutes} min` : '—'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Section title="Top earners" icon={<Trophy size={14} className="text-amber-400" />}>
          {lb.isLoading ? (
            <Loading />
          ) : !lb.data?.top_earners.length ? (
            <Empty message="No deliveries in this period." />
          ) : (
            lb.data.top_earners.map((d, i) => (
              <DriverLeaderRow
                key={d.id}
                rank={i + 1}
                driver={d}
                onClick={() => setPerfDriverId(d.id)}
                stat={
                  <div className="text-right">
                    <p className="text-white font-bold tabular-nums">{formatJOD(d.earnings_fils)}</p>
                    {d.tips_fils > 0 && (
                      <p className="text-[10px] text-green-400 tabular-nums">+{formatJOD(d.tips_fils)} tips</p>
                    )}
                  </div>
                }
                sub={`${d.orders_count} ${d.orders_count === 1 ? 'order' : 'orders'}`}
              />
            ))
          )}
        </Section>

        <Section title="Best on-time rate" icon={<Clock size={14} className="text-green-400" />} hint={
          lb.data ? `Min ${lb.data.min_sample_for_on_time} deliveries` : undefined
        }>
          {lb.isLoading ? (
            <Loading />
          ) : !lb.data?.best_on_time.length ? (
            <Empty message="Not enough data yet." />
          ) : (
            lb.data.best_on_time.map((d, i) => (
              <DriverLeaderRow
                key={d.id}
                rank={i + 1}
                driver={d}
                onClick={() => setPerfDriverId(d.id)}
                stat={
                  <span className={`font-bold tabular-nums ${
                    (d.on_time_rate ?? 0) >= 90 ? 'text-green-400' : 'text-amber-400'
                  }`}>{d.on_time_rate?.toFixed(1)}%</span>
                }
                sub={`${d.sample_size} timed · avg ${d.avg_delivery_minutes?.toFixed(1) ?? '—'}m`}
              />
            ))
          )}
        </Section>

        <Section title="Top rated" icon={<Star size={14} className="text-amber-400" />} hint="Min 3 ratings">
          {lb.isLoading ? (
            <Loading />
          ) : (() => {
            const rated = (lb.data?.top_earners ?? [])
              .filter(d => d.rating_count >= 3 && d.avg_rating != null)
              .sort((a, b) => (b.avg_rating! - a.avg_rating!) || (b.rating_count - a.rating_count))
              .slice(0, 10)
            return rated.length === 0 ? (
              <Empty message="Not enough ratings yet." />
            ) : (
              rated.map((d, i) => (
                <DriverLeaderRow
                  key={d.id}
                  rank={i + 1}
                  driver={d}
                  onClick={() => setPerfDriverId(d.id)}
                  stat={
                    <div className="flex items-center gap-1">
                      <Star size={13} className="text-amber-400 fill-amber-400" />
                      <span className="text-amber-400 font-bold tabular-nums">{d.avg_rating!.toFixed(1)}</span>
                    </div>
                  }
                  sub={`${d.rating_count} rating${d.rating_count === 1 ? '' : 's'}`}
                />
              ))
            )
          })()}
        </Section>
      </div>

      <Section title="SLA breaches — recent slowest deliveries" icon={<AlertCircle size={14} className="text-[#C41E3A]" />}>
        {lb.isLoading ? (
          <Loading />
        ) : !lb.data?.slowest_deliveries.length ? (
          <Empty message="No deliveries exceeded the SLA in this period. 🎉" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                {['Order', 'Ambassador', 'Address', 'Delivered at', 'Duration'].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lb.data.slowest_deliveries.map(o => (
                <tr key={o.id} className="border-b border-[#222]">
                  <td className="px-3 py-2 text-white">#{o.order_number}</td>
                  <td className="px-3 py-2 text-gray-400">{o.driver_name ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-500 max-w-[280px] truncate">
                    {o.delivery_address ? (
                      <span className="flex items-center gap-1.5">
                        <MapPin size={11} className="text-gray-600 shrink-0" />
                        <span className="truncate">{o.delivery_address}</span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs">
                    {o.delivered_at ? formatDateTime(o.delivered_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-[#C41E3A] tabular-nums font-semibold">
                    {o.delivery_minutes.toFixed(1)} min
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <div className="mt-4">
        <Section
          title="Payout summary"
          icon={<DollarSign size={14} className="text-green-400" />}
          actions={
            <button
              onClick={() => downloadPayoutCsv(payout.data, range)}
              disabled={!payout.data?.rows.length}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors disabled:opacity-40"
            >
              <Download size={13} /> Export CSV
            </button>
          }
        >
          {payout.isLoading ? (
            <Loading />
          ) : !payout.data ? (
            <Empty message="Failed to load." />
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                    <th className="px-3 py-2 text-left font-medium">Ambassador</th>
                    <th className="px-3 py-2 text-left font-medium">Phone</th>
                    <th className="px-3 py-2 text-right font-medium">Deliveries</th>
                    <th className="px-3 py-2 text-right font-medium">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {payout.data.rows.map(r => (
                    <tr key={r.id} className={`border-b border-[#222] ${
                      r.orders_count === 0 ? 'opacity-50' : ''
                    }`}>
                      <td className="px-3 py-2 text-white">{r.full_name}</td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-xs">{r.phone}</td>
                      <td className="px-3 py-2 text-gray-300 text-right tabular-nums">{r.orders_count}</td>
                      <td className="px-3 py-2 text-white text-right tabular-nums font-semibold">
                        {formatJOD(r.earnings_fils)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#C41E3A]/10">
                    <td className="px-3 py-2.5 text-[#C41E3A] font-semibold" colSpan={2}>Grand total</td>
                    <td className="px-3 py-2.5 text-[#C41E3A] text-right tabular-nums font-semibold">
                      {payout.data.grand_total.orders}
                    </td>
                    <td className="px-3 py-2.5 text-[#C41E3A] text-right tabular-nums font-bold">
                      {formatJOD(payout.data.grand_total.earnings_fils)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </>
          )}
        </Section>
      </div>

      {perfDriverId && (
        <AmbassadorPerformanceModal driverId={perfDriverId} onClose={() => setPerfDriverId(null)} />
      )}
    </div>
  )
}

function RangeTabs({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="inline-flex bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg p-0.5">
      {(['today', '7d', '30d', 'all'] as Range[]).map(r => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === r ? 'bg-[#C41E3A] text-white' : 'text-gray-400 hover:text-white'
          }`}
        >
          {RANGE_LABELS[r]}
        </button>
      ))}
    </div>
  )
}

function SummaryTile({ icon, label, value, accent }: {
  icon: React.ReactNode; label: string; value: string; accent?: boolean
}) {
  return (
    <div className={`rounded-lg p-4 border ${
      accent ? 'bg-[#C41E3A]/10 border-[#C41E3A]/30' : 'bg-[#1a1a1a] border-[#2e2e2e]'
    }`}>
      <div className="flex items-center gap-2 mb-1.5 text-gray-500">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className={`text-xl font-bold tabular-nums ${accent ? 'text-[#C41E3A]' : 'text-white'}`}>
        {value}
      </p>
    </div>
  )
}

function Section({ title, icon, hint, actions, children }: {
  title: string; icon?: React.ReactNode; hint?: string;
  actions?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2e2e2e]">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-sm font-semibold text-white">{title}</h2>
        </div>
        <div className="flex items-center gap-3">
          {hint && <span className="text-xs text-gray-600">{hint}</span>}
          {actions}
        </div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  )
}

function DriverLeaderRow({ rank, driver, stat, sub, onClick }: {
  rank: number; driver: DriverRow; stat: React.ReactNode; sub: string; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors text-left"
    >
      <span className={`w-6 text-xs text-center tabular-nums ${
        rank === 1 ? 'text-amber-400 font-bold' : 'text-gray-500'
      }`}>{rank}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{driver.full_name}</p>
        <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
      </div>
      {stat}
    </button>
  )
}

function Loading() {
  return <p className="text-sm text-gray-600 py-6 text-center">Loading…</p>
}

function Empty({ message }: { message: string }) {
  return <p className="text-sm text-gray-500 py-6 text-center">{message}</p>
}

function downloadPayoutCsv(data: PayoutResponse | undefined, range: Range) {
  if (!data) return
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`
  const header = ['Ambassador', 'Phone', 'Deliveries', 'Payout (JD)'].join(',')
  const body = data.rows.map(r =>
    [escape(r.full_name), escape(r.phone), String(r.orders_count), (r.earnings_fils / 1000).toFixed(3)].join(',')
  ).join('\n')
  const total = ['"Grand total"', '""', String(data.grand_total.orders),
    (data.grand_total.earnings_fils / 1000).toFixed(3)].join(',')
  const csv = `${header}\n${body}\n${total}\n`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `drinx-payout-${range}-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
